/**
 * GPU / 内存全局监控模块
 *
 * 职责：
 *  - WebGL 上下文丢失 / 恢复监听
 *  - rAF 帧率采样（页面隐藏时自动暂停）
 *  - JS 堆内存定时采样
 *  - PerformanceObserver 长任务监听
 *  - 崩溃前兆检测（crash_precursor）
 *  - pagehide 兜底上报（page_unload_snapshot）
 *  - 自适应采样频率（高危时降频）
 *
 * 使用方式：
 *  GPUMemoryMonitor.getInstance().start();  // 在 main.tsx APM 初始化后调用
 */

import apmClient from '@apmplus/web';
import { reportCustomEvent, reportCustomLog, getSessionId } from '@/config/apm';
import { dumpErrorDetail } from '@/observability/snapshot';

// ─── 浏览器非标准 API 类型补充 ────────────────────────────────────────────────

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  readonly memory?: MemoryInfo;
}

interface NavigatorWithMemory extends Navigator {
  readonly deviceMemory?: number;
}

interface PerformanceLongTaskTiming extends PerformanceEntry {
  readonly attribution: ReadonlyArray<{
    name: string;
    /** 长任务发生在 iframe 内时的容器信息（主文档为 undefined） */
    containerType?: string;
    containerSrc?: string;
    containerId?: string;
    containerName?: string;
  }>;
}

// ─── 常量 ────────────────────────────────────────────────────────────────────

const MEMORY_INTERVAL_NORMAL = 30_000;   // 正常模式：30s
const MEMORY_INTERVAL_CRITICAL = 5_000;  // 高危模式：5s
const FPS_INTERVAL_NORMAL = 10_000;      // 正常模式：10s
const FPS_INTERVAL_CRITICAL = 3_000;     // 高危模式：3s
const FPS_WINDOW_SIZE = 60;              // 保留最近 60 帧间隔
const LOW_FPS_THRESHOLD = 20;            // fps < 20 视为低帧率（前兆判定用，未改动）
const LOW_FPS_DURATION_MS = 5_000;       // 持续 5s 才计入前兆
// FPS 判定阈值（2026-08-27 实测修正，占 92.3% 上报量的噪声根因）：
// 30fps 是浏览器正常工作状态（Safari/Mac 省电、低电量、后台标签会主动锁 30Hz），
// 旧阈值 fps<30 恰好把「系统降频」判为「卡顿」。改用电影帧率 24 作为可感卡顿分界。
const FPS_WARNING_THRESHOLD = 24;        // avgFps < 24 才计入 warning
const FPS_CRITICAL_THRESHOLD = 15;       // avgFps < 15 才计入 critical
// 30fps 的正常帧间隔恰为 33.3ms，旧阈值 33ms 会把稳定 30fps 判定为「每帧都掉帧」；
// 改为 50ms（对应 <20fps）避免把系统降频误判为掉帧。
const FRAME_DROP_THRESHOLD_MS = 50;
// 稳定低帧率判据：系统降频的特征是「稳定」（窗口内 max-min 差值很小），真卡顿的特征是「波动」。
// 持续满足此稳定性超过该时长，判定为系统降频而非卡顿，跳过上报。
const STABLE_LOW_FPS_JITTER_MS = 3;
const STABLE_LOW_FPS_DURATION_MS = 30_000;
const HEAP_WARNING_RATIO = 0.8;          // 堆使用率 > 80% 触发 memory_warning
const HEAP_PRECURSOR_RATIO = 0.75;       // 堆使用率 > 75% 计入前兆
const MAX_MEMORY_WARN_COUNT = 3;         // 同会话最多上报 3 次 memory_warning
const VIDEO_PRECURSOR_COUNT = 15;        // 活跃视频 > 15 计入前兆
const DOM_PRECURSOR_COUNT = 5_000;       // DOM 节点 > 5000 计入前兆
const HEAP_GROWTH_SPIKE_RATE = 2;        // 增长率 > 2MB/s 视为 spike
const HEAP_GROWTH_GROWING_RATE = 0.5;    // 增长率 > 0.5MB/s 视为 growing
const LONG_TASK_REPORT_THRESHOLD_MS = 300; // 长任务 > 300ms 才上报

// 激进低频采样：正常态完全不采样上报，异常才高频（见 adjustSamplingRate）
// 崩溃确认标记（2026-09-02 修复多标签页误报）：
// 旧实现用单一 localStorage 标记判「上次是否干净退出」，同源下所有标签页共享该标记——
// A 标签页正常开着（尚未 pagehide）时若开了 B 标签页，B 一启动就会读到"已启动但未标记干净"，
// 把 A 仍在运行误判为"上次异常退出"，与真实崩溃率无关，纯属并发标签页的竞态误报。
// 改为「每标签页独立 id（存 sessionStorage，不跨标签共享）+ 集中注册表（存 localStorage）
// + 心跳」：只有"这个 id 自己上次没有清理掉注册"或"某个 id 心跳超时没人认领"才判定崩溃，
// 不会因为同时开着别的标签页而误判。
const TAB_ID_KEY = 'amai_tab_id';                      // sessionStorage：本标签页唯一 id
const TAB_REGISTRY_KEY = 'amai_tab_registry';          // localStorage：{ [tabId]: 最近心跳时间戳 }
const TAB_HEARTBEAT_INTERVAL_MS = 30_000;              // 心跳间隔
// 阈值须显著大于心跳间隔，容忍后台标签页被浏览器节流定时器（通常降到 ~1 次/分钟）
const TAB_STALE_THRESHOLD_MS = 180_000;

// 优雅退出标记（2026-09-14 修复：正常卸载却被误判为崩溃）
// 背景：注册表是单 key 的「读-改-写」，多标签页并发写回会互相覆盖 —— 页面正常卸载时
// 「删除自己记录」这一步可能被另一个标签页的写回覆盖，留下陈旧记录 → 下次启动被判为崩溃
// （但该会话其实上报过 page_unload_snapshot、且无任何内存采样，明显不是真崩）。
// 方案：卸载时额外写一个「每个 tabId 一个独立 key」的标记，独立 key 不会互相覆盖，
// 是「本次正常退出」最可靠的证据。判定时若标记时间不早于最后心跳 → 判为正常退出。
const TAB_CLEAN_EXIT_PREFIX = 'amai_clean_exit:';
// 标记 TTL：判定用完后即删，启动时再兜底清理超过此时长的残留，防止长期累积
const CLEAN_EXIT_TTL_MS = 24 * 60 * 60 * 1000;

// 长任务归因：只认可「长任务开始前该窗口内」的最近一次交互
const INTERACTION_ATTRIBUTION_WINDOW_MS = 3_000;
// 参与归因的交互类型（pointerdown 覆盖鼠标/触摸，keydown 覆盖键盘，wheel 覆盖滚动缩放）
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;

// ─── 类型 ────────────────────────────────────────────────────────────────────

interface SessionSnapshot {
  usedHeapMb: number;
  heapUsageRatio: number;
  fpsLast: number;
  activeVideoCount: number;
  domNodeCount: number;
  sessionDurationS: number;
  gpuRenderer: string;
  page: string;
  deviceMemoryGb: string;
  hadPrecursor: boolean;
  webglLostCount: number;
}

// ─── 标签页注册表（localStorage，跨标签页共享）─────────────────────────────────

/**
 * 标签页注册表：{ [tabId]: 心跳条目 }
 *
 * 兼容说明：旧版本只存「数字时间戳」，新版本存 { ts, visible }。
 * 之所以要记 visible：后台标签页会被浏览器**冻结定时器**、甚至被**系统回收（discarded）**，
 * 这两种情况心跳同样会停 / 注册表同样不会清理，但**页面并没有崩溃**。
 * 若不加区分，就会把「切到后台再回来」「被系统回收」大量误判为崩溃
 * （表现为 crash_confirmed 很多、但 crash_precursor 极少——因为根本不是性能问题）。
 * 旧数据没有 visible，按「可见」处理（保守：宁可报，也不漏真崩溃）。
 */
interface TabEntry {
  ts: number;
  visible: boolean;
  /**
   * 该标签页实例的会话标识（sessionId）。
   * 关键作用：crash_confirmed 是在**新会话**里补报的，若不带上「崩掉的那个会话」的
   * sessionId，排查时拿着新会话 id 去查 —— 永远查不到崩溃现场（前兆/日志/操作序列
   * 都挂在旧会话下）。此字段就是那根「串联崩溃现场」的线。
   */
  sid?: string;
  /**
   * 该标签页最后一次心跳时所在的页面路径。
   * 作用：crash_confirmed 的 page 字段是**新会话**的页面，答不了「崩在哪个页面」。
   * 此字段记下「崩掉的那个会话」的最后页面，补报时一并带出。
   */
  page?: string;
}

type TabRegistry = Record<string, number | TabEntry>;

function normalizeEntry(value: number | TabEntry | undefined): TabEntry {
  if (typeof value === 'number') return { ts: value, visible: true }; // 旧格式：按可见处理
  if (value && typeof value === 'object') {
    return {
      ts: value.ts ?? 0,
      visible: value.visible !== false,
      sid: value.sid,
      page: value.page,
    };
  }
  return { ts: 0, visible: true };
}

function readTabRegistry(): TabRegistry {
  try {
    const raw = localStorage.getItem(TAB_REGISTRY_KEY);
    return raw ? (JSON.parse(raw) as TabRegistry) : {};
  } catch {
    return {};
  }
}

function writeTabRegistry(registry: TabRegistry): void {
  try {
    localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(registry));
  } catch {
    // 隐私模式/容量满，静默忽略
  }
}

// ─── 优雅退出标记（独立 key，避开注册表的并发写竞态）─────────────────────────

function writeCleanExitMarker(tabId: string): void {
  try {
    localStorage.setItem(TAB_CLEAN_EXIT_PREFIX + tabId, String(Date.now()));
  } catch {
    // 隐私模式/容量满，静默忽略
  }
}

function readCleanExitMarker(tabId: string): number {
  try {
    return Number(localStorage.getItem(TAB_CLEAN_EXIT_PREFIX + tabId)) || 0;
  } catch {
    return 0;
  }
}

function clearCleanExitMarker(tabId: string): void {
  try {
    localStorage.removeItem(TAB_CLEAN_EXIT_PREFIX + tabId);
  } catch {
    // 忽略
  }
}

/**
 * 该记录是否「正常退出过」：存在优雅退出标记，且退出时间不早于最后心跳。
 * 时间比较可挡住「标记是上一次会话残留」的情况（旧标记早于本次心跳 → 不采信）。
 */
function exitedCleanly(tabId: string, lastHeartbeatTs: number): boolean {
  const at = readCleanExitMarker(tabId);
  return at !== 0 && at >= lastHeartbeatTs;
}

/** 兜底清理过期的优雅退出标记（正常情况判定时已删除，此处防长期累积） */
function pruneCleanExitMarkers(now: number): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(TAB_CLEAN_EXIT_PREFIX)) continue;
      const at = Number(localStorage.getItem(key)) || 0;
      if (now - at > CLEAN_EXIT_TTL_MS) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    // 忽略
  }
}

/**
 * 元素摘要（长任务归因用）：`tag#id.class|文本片段`，截断到 64 字符。
 * 只做内存字符串拼接、不上报，开销可忽略。
 */
function summarizeElement(el: Element | null): string {
  if (!el) return 'unknown';
  const tag = el.tagName ? el.tagName.toLowerCase() : 'unknown';
  const id = (el as HTMLElement).id ? `#${(el as HTMLElement).id}` : '';
  const rawCls = (el as HTMLElement).className;
  const cls = typeof rawCls === 'string' && rawCls
    ? `.${rawCls.trim().split(/\s+/).slice(0, 2).join('.')}`
    : '';
  const text = (el.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 16);
  return `${tag}${id}${cls}${text ? `|${text}` : ''}`.slice(0, 64);
}

// ─── 主类 ────────────────────────────────────────────────────────────────────

export class GPUMemoryMonitor {
  private static instance: GPUMemoryMonitor | null = null;

  // 生命周期
  private started = false;
  private sessionStartTime = Date.now();

  // 崩溃检测：本标签页唯一 id + 心跳定时器
  private tabId = '';
  private tabHeartbeatTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * 可见性变化处理（箭头函数绑定 this）。
   * 切后台 / 回前台时**立即**刷新注册表里的 visible，不等下一次心跳——
   * 否则切后台后被立刻冻结，最后一次心跳仍记着 visible=true，仍会被误判为崩溃。
   */
  /** 写入/刷新本标签页的心跳条目（含可见性与本会话 sid） */
  private writeHeartbeat(): void {
    try {
      if (!this.tabId) return;
      const registry = readTabRegistry();
      registry[this.tabId] = {
        ts: Date.now(),
        visible: document.visibilityState !== 'hidden',
        // 记下本会话 id：若本会话之后崩溃，新会话可据此定位到它的现场
        sid: getSessionId(),
        // 记下当前页面：崩溃补报时才能回答「崩在哪个页面」
        page: this.getPage(),
      };
      writeTabRegistry(registry);
    } catch {
      // 忽略
    }
  }

  /** 可见性变化：立即刷新心跳条目（不等下一次心跳，否则被冻结前仍记 visible=true） */
  private handleVisibilityChange = (): void => {
    this.writeHeartbeat();
  };

  // GPU 上下文
  private gpuRenderer = 'unknown';
  private contextLostTime: number | null = null;
  private contextLostCount = 0;
  private hadPrecursor = false;

  // 设备信息（启动时一次性读取，避免每次采样重复读取）
  private cpuCores: string = String(navigator.hardwareConcurrency ?? 'unknown');

  // rAF / FPS
  private rafHandle: number | null = null;
  private lastFrameTime = 0;
  private fpsWindow: number[] = [];       // 帧间隔（ms）滑动窗口
  private frameDropCount = 0;             // 当前采样窗口内掉帧次数
  private lowFpsStartTime: number | null = null;
  private stableLowFpsStartTime: number | null = null; // 系统降频（稳定低帧率）判据起始时间
  private currentFps = 60;

  // 定时器
  private memoryTimer: ReturnType<typeof setInterval> | null = null;
  private fpsReportTimer: ReturnType<typeof setInterval> | null = null;

  // 内存
  private lastHeapMb = 0;
  private lastHeapSampleTime = 0;
  private memoryWarnCount = 0;
  private inCriticalMode = false;

  // 前兆
  private precursorFired = false;

  // fps_drop 去重：每次连续低帧率只上报一条，帧率恢复后重置
  private fpsDroppingReported = false;

  // 会话聚合（用于 session_summary）
  private fpsSamples: number[] = [];
  private memorySamples: number[] = [];
  private longTaskCount = 0;
  private longTaskTotalMs = 0;

  // 最近一次用户交互（长任务近似归因：卡顿多由「操作 → 触发重计算」引发）
  private lastInteraction: { type: string; target: string; at: number } | null = null;
  private interactionTrackingAttached = false;
  private interactionHandler: ((e: Event) => void) | null = null;

  // 激进低频采样门控：正常态关闭（零采样零上报），异常态打开走高频
  private memorySamplingActive = false;
  private fpsReportingActive = false;

  // ─── 单例 ──────────────────────────────────────────────────────────────────

  static getInstance(): GPUMemoryMonitor {
    if (!GPUMemoryMonitor.instance) {
      GPUMemoryMonitor.instance = new GPUMemoryMonitor();
    }
    return GPUMemoryMonitor.instance;
  }

  private constructor() {}

  // ─── 公开方法 ──────────────────────────────────────────────────────────────

  start(): void {
    if (this.started) return;
    this.started = true;

    // 硬崩溃补报：扫描标签页注册表，报出未清理（判定为异常退出）的条目，再登记自己
    this.checkAndReportPreviousCrash();
    this.startTabHeartbeat();

    this.initGPURenderer();
    this.setupWebGLListeners();
    this.setupVisibilityListener();
    this.setupPageHideListener();
    this.setupLongTaskObserver();
    this.setupInteractionTracking();
    this.startRAFLoop();
    this.startMemorySampling();
    this.startFpsReporting();
  }

  // ─── 硬崩溃事后补报（下次启动时）─────────────────────────────────────────
  // 真·标签页崩溃/进程被 kill 时 JS 主线程来不及上报 crash_precursor 或
  // gpu_context_lost；每个标签页在注册表里登记自己的 id + 心跳，正常 pagehide 时移除。
  // 下次任意标签页启动时扫描注册表：
  //   ① 发现自己上次的 id 还在（同一标签页被浏览器崩溃恢复）→ 上次必定没走到 pagehide，立即判定；
  //   ② 发现别的 id 心跳超时未清理（阈值远大于心跳间隔，容忍后台节流）→ 判定为那个标签页异常退出。
  // 二者都只依据"某个具体标签页自己的记录"，不会因为同时开着别的、仍在正常运行的标签页而误判。
  private checkAndReportPreviousCrash(): void {
    try {
      this.tabId = this.getOwnTabId();
      const registry = readTabRegistry();
      const now = Date.now();
      let crashCount = 0;

      // 本次加载是否由「被系统回收（discarded）」恢复。
      // wasDiscarded 是浏览器提供的标志：页面上次在后台被丢弃以节省资源，
      // 本次是恢复出来的 —— 这属于系统回收，**不是崩溃**。
      const discarded =
        (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true;

      // 收集被判为崩溃的「旧会话」标识/页面，供后续串联崩溃现场
      const prevSessions: string[] = [];
      const prevPages: string[] = [];
      // 每条判定对应的「心跳停止时长(s)」，随 crash_confirmed 一并上报：
      // 排查时可据此识别可疑判定 —— 若大量判定集中在 180~300s（刚过阈值），
      // 说明存在「残留记录型误判」；分布分散则更像真崩溃。同时供后续阈值调优。
      const heartbeatAges: number[] = [];

      // 自己上次的记录还在 → 先看有没有「优雅退出标记」：
      // pagehide 写标记用的是独立 key，不会被并发写覆盖；标记时间不早于最后心跳，
      // 说明上次其实是正常退出（只是注册表清理被覆盖/失败）→ 不算崩溃。
      if (Object.prototype.hasOwnProperty.call(registry, this.tabId)) {
        const entry = normalizeEntry(registry[this.tabId]);
        // 真崩溃 = 不是被系统回收，且上次最后处于前台（后台会被冻结/节流，心跳同样会停）
        if (!discarded && entry.visible && !exitedCleanly(this.tabId, entry.ts)) {
          crashCount += 1;
          if (entry.sid) prevSessions.push(entry.sid);
          if (entry.page) prevPages.push(entry.page);
          heartbeatAges.push(Math.max(0, Math.round((now - entry.ts) / 1000)));
        }
        delete registry[this.tabId];
        clearCleanExitMarker(this.tabId); // 判定完即清，避免标记长期累积
      }

      // 别的标签页心跳超时未清理 → 判定异常退出，清理掉避免重复计数
      for (const [id, raw] of Object.entries(registry)) {
        const entry = normalizeEntry(raw);
        if (now - entry.ts > TAB_STALE_THRESHOLD_MS) {
          // 后台（hidden）超时的记录：可能是浏览器冻结定时器或系统回收，不算真崩溃
          if (entry.visible && !exitedCleanly(id, entry.ts)) {
            crashCount += 1;
            if (entry.sid) prevSessions.push(entry.sid);
            if (entry.page) prevPages.push(entry.page);
            heartbeatAges.push(Math.max(0, Math.round((now - entry.ts) / 1000)));
          }
          delete registry[id];
          clearCleanExitMarker(id);
        }
      }

      // 兜底：清理过期残留标记
      pruneCleanExitMarkers(now);

      for (let i = 0; i < crashCount; i += 1) {
        reportCustomEvent(
          'crash_confirmed',
          {
            count: 1,
            // 该条判定对应的心跳停止时长(s)：-1 = 未知。
            // 数据侧自证：用它判断「刚过 180s 阈值」的可疑判定（疑似残留记录误判）。
            heartbeat_age_s: heartbeatAges[i] ?? -1,
          },
          {
            page: this.getPage(),
            gpu_renderer: this.gpuRenderer,
            device_memory_gb: String((navigator as NavigatorWithMemory).deviceMemory ?? 'unknown'),
            cpu_cores: this.cpuCores,
            // 仅「真崩溃」才会走到这里：已排除三类误判 ——
            // ① 系统回收（wasDiscarded）② 后台被冻结/节流（visible=false）
            // ③ 正常卸载但注册表清理失败（优雅退出标记）。
            reason: 'crash',
          },
        );
      }

      // 🔗 串联崩溃现场：把「崩掉的那个会话」的 id 单独发一条日志带出来。
      // 为什么走日志而非事件字段：sid 是高基数（每个会话都不同），进 categories
      // 会让看板每条自成一类、分组失效；走 content 供单条排查取用。
      // 用法：在「自定义日志」搜 crash_confirmed → 取 prevSessionIds →
      //       用它反查该会话的 crash_precursor / js_error / 操作序列。
      if (prevSessions.length > 0 || prevPages.length > 0) {
        try {
          dumpErrorDetail('crash_confirmed', {
            prevSessionIds: prevSessions.join(','),
            // 崩溃会话的最后页面：crash_confirmed 自身的 page 字段是【本会话】的页面，
            // 不能回答「崩在哪个页面」，此字段才是崩溃现场所在的页面。
            prevPages: prevPages.join(','),
            note: '上次崩溃所属会话/页面 —— 用 sessionId 反查该会话的 crash_precursor / js_error / 操作序列',
          });
        } catch {
          // 忽略
        }
      }

      if (crashCount > 0) {
        apmClient.getSender()?.flush();
      }

      // 登记自己（心跳存活期间，不会被其它标签页判定为崩溃）
      this.writeHeartbeat();
    } catch {
      // localStorage/sessionStorage 不可用时静默忽略
    }
  }

  private getOwnTabId(): string {
    const fallback = () => `t_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    try {
      let id = sessionStorage.getItem(TAB_ID_KEY);
      if (!id) {
        id = fallback();
        sessionStorage.setItem(TAB_ID_KEY, id);
      }
      return id;
    } catch {
      return fallback();
    }
  }

  private startTabHeartbeat(): void {
    if (this.tabHeartbeatTimer !== null) return;
    this.tabHeartbeatTimer = setInterval(() => {
      this.writeHeartbeat();
    }, TAB_HEARTBEAT_INTERVAL_MS);

    // 切后台 / 回前台立即刷新可见性（不等心跳）
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private stopTabHeartbeat(): void {
    if (this.tabHeartbeatTimer !== null) {
      clearInterval(this.tabHeartbeatTimer);
      this.tabHeartbeatTimer = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  stop(): void {
    this.stopRAFLoop();
    this.stopMemorySampling();
    this.stopFpsReporting();
    this.stopTabHeartbeat();
    this.teardownInteractionTracking();
    this.started = false;
  }

  /** 供 HoverVideoPlayer 调用：上报视频资源生命周期（10% 采样） */
  reportVideoLifecycle(
    action: 'load' | 'unload',
    trigger: 'viewport_enter' | 'viewport_leave' | 'unmount',
  ): void {
    if (Math.random() >= 0.1) return; // 降采样，减少高频上报
    const activeVideoCount = this.getActiveVideoCount();
    reportCustomEvent(
      'video_resource_lifecycle',
      { active_video_count: activeVideoCount },
      { action, trigger, page: this.getPage() },
    );
  }

  // ─── GPU 上下文 ────────────────────────────────────────────────────────────

  private initGPURenderer(): void {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl');
      if (gl && 'getExtension' in gl) {
        const ext = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
        if (ext) {
          this.gpuRenderer = (gl as WebGLRenderingContext).getParameter(ext.UNMASKED_RENDERER_WEBGL) ?? 'unknown';
        }
      }
    } catch {
      // 获取 GPU 信息失败不影响监控启动
    }
  }

  private setupWebGLListeners(): void {
    window.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.contextLostTime = Date.now();
      this.contextLostCount += 1;

      const mem = this.getMemoryInfo();
      reportCustomEvent(
        'gpu_context_lost',
        { count: 1 },
        {
          page: this.getPage(),
          gpu_renderer: this.gpuRenderer,
          memory_at_crash: String(mem ? Math.round(mem.usedJSHeapSize / 1_048_576) : 0),
          fps_at_crash: String(Math.round(this.currentFps)),
          active_video_at_crash: String(this.getActiveVideoCount()),
          session_duration_s: String(this.getSessionDurationS()),
          had_precursor: this.hadPrecursor ? 'true' : 'false',
          network_type: (navigator as any).connection?.effectiveType ?? 'unknown',
          cpu_cores: this.cpuCores,
        },
      );
      // 崩溃时立即 flush，不等批量
      apmClient.getSender()?.flush();
    }, true);

    window.addEventListener('webglcontextrestored', () => {
      const lostDurationMs = this.contextLostTime != null
        ? Date.now() - this.contextLostTime
        : 0;
      this.contextLostTime = null;

      reportCustomEvent(
        'gpu_context_restored',
        { count: 1, lost_duration_ms: lostDurationMs },
        { page: this.getPage() },
      );
    }, true);
  }

  // ─── rAF / FPS ─────────────────────────────────────────────────────────────

  private startRAFLoop(): void {
    if (this.rafHandle !== null) return;
    this.lastFrameTime = performance.now();
    const tick = (timestamp: number) => {
      this.onRAFTick(timestamp);
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private stopRAFLoop(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  private onRAFTick(timestamp: number): void {
    const delta = timestamp - this.lastFrameTime;
    this.lastFrameTime = timestamp;

    if (delta <= 0) return;

    // 维护滑动窗口
    this.fpsWindow.push(delta);
    if (this.fpsWindow.length > FPS_WINDOW_SIZE) {
      this.fpsWindow.shift();
    }

    // 掉帧计数
    if (delta > FRAME_DROP_THRESHOLD_MS) {
      this.frameDropCount += 1;
    }

    // 实时 fps（基于最近一帧，供 adjustSamplingRate 等需要快速响应的场景使用）
    this.currentFps = 1_000 / delta;

    // 低帧率持续时间追踪：改用窗口均值（同 P0-21 对 fps_snapshot 的修法），
    // 消除单帧抖动误判——否则 fps_drop / crash_precursor 的 lowFps 信号仍会被
    // 一次卡顿的瞬时帧打断计时，导致「持续 5s」的判定被单帧噪声反复重置。
    const avgFps = this.computeAvgFps();
    if (avgFps < LOW_FPS_THRESHOLD) {
      if (this.lowFpsStartTime === null) {
        this.lowFpsStartTime = Date.now();
      }
    } else {
      this.lowFpsStartTime = null;
      this.fpsDroppingReported = false; // 帧率恢复，重置去重标志
    }
  }

  private startFpsReporting(): void {
    this.stopFpsReporting();
    this.fpsReportTimer = setInterval(() => {
      this.reportFpsSnapshot();
    }, this.inCriticalMode ? FPS_INTERVAL_CRITICAL : FPS_INTERVAL_NORMAL);
  }

  private stopFpsReporting(): void {
    if (this.fpsReportTimer !== null) {
      clearInterval(this.fpsReportTimer);
      this.fpsReportTimer = null;
    }
  }

  private reportFpsSnapshot(): void {
    // 激进低频：正常态门控关闭时零上报
    if (!this.fpsReportingActive) return;
    if (this.fpsWindow.length === 0) return;

    const avgDelta = this.fpsWindow.reduce((a, b) => a + b, 0) / this.fpsWindow.length;
    const maxDelta = Math.max(...this.fpsWindow);
    const minDelta = Math.min(...this.fpsWindow);
    const fps = Math.round(this.currentFps);
    const avgFps = Math.round(1_000 / avgDelta);
    const minFps = Math.round(1_000 / maxDelta);
    const frameDropCount = this.frameDropCount;
    this.frameDropCount = 0; // 重置计数

    // 稳定低帧率判据：系统降频（如 Mac 省电锁 30Hz）特征是「稳定」，真卡顿特征是「波动」。
    // 窗口内帧间隔抖动 < 3ms 且持续 ≥ 30s，判定为系统降频，本次不计入 warning/critical。
    const isStableWindow = maxDelta - minDelta < STABLE_LOW_FPS_JITTER_MS;
    if (isStableWindow) {
      if (this.stableLowFpsStartTime === null) this.stableLowFpsStartTime = Date.now();
    } else {
      this.stableLowFpsStartTime = null;
    }
    const isSystemThrottle = this.stableLowFpsStartTime !== null &&
      Date.now() - this.stableLowFpsStartTime >= STABLE_LOW_FPS_DURATION_MS;

    // 判定依据改用 avgFps（60 帧窗口均值），消除单帧抖动误判；阈值改为 24/15（电影帧率/严重卡顿）
    const level = isSystemThrottle ? 'normal'
      : avgFps < FPS_CRITICAL_THRESHOLD ? 'critical'
      : avgFps < FPS_WARNING_THRESHOLD ? 'warning' : 'normal';
    const videoCount = this.getActiveVideoCount();
    const compositorPressure = (fps < 45 && frameDropCount > 5) ? 'high'
      : (fps < 45 || frameDropCount > 2) ? 'medium' : 'low';

    // normal 级别跳过上报，warning/critical 才有诊断价值
    if (level !== 'normal') {
      reportCustomEvent(
        'fps_snapshot',
        { fps, avg_fps: avgFps, min_fps: minFps, frame_drop_count: frameDropCount },
        {
          page: this.getPage(),
          level,
          video_active_count: String(videoCount),
          waterfall_item_count: String(this.getWaterfallItemCount()),
          compositor_pressure: compositorPressure,
        },
      );
    }

    // 累积会话聚合样本（不受 level 过滤影响）
    this.fpsSamples.push(fps);

    // fps 持续低于阈值超过 5s 时上报 fps_drop（每次连续低帧率只上报一条）
    if (
      this.lowFpsStartTime !== null &&
      Date.now() - this.lowFpsStartTime > LOW_FPS_DURATION_MS &&
      !this.fpsDroppingReported
    ) {
      this.fpsDroppingReported = true;
      reportCustomEvent(
        'fps_drop',
        { fps, duration_ms: Date.now() - this.lowFpsStartTime },
        {
          page: this.getPage(),
          video_active_count: String(videoCount),
          waterfall_item_count: String(this.getWaterfallItemCount()),
        },
      );
    }

    this.checkPrecursor();
    this.adjustSamplingRate();
  }

  // ─── 内存采样 ──────────────────────────────────────────────────────────────

  private startMemorySampling(): void {
    this.stopMemorySampling();
    this.memoryTimer = setInterval(() => {
      this.sampleMemory();
    }, this.inCriticalMode ? MEMORY_INTERVAL_CRITICAL : MEMORY_INTERVAL_NORMAL);
  }

  private stopMemorySampling(): void {
    if (this.memoryTimer !== null) {
      clearInterval(this.memoryTimer);
      this.memoryTimer = null;
    }
  }

  private sampleMemory(): void {
    // 激进低频：正常态门控关闭时零采样
    if (!this.memorySamplingActive) return;
    const mem = this.getMemoryInfo();
    if (!mem) return; // performance.memory 仅 Chrome 支持

    const now = Date.now();
    const usedMb = mem.usedJSHeapSize / 1_048_576;
    const totalMb = mem.totalJSHeapSize / 1_048_576;
    const limitMb = mem.jsHeapSizeLimit / 1_048_576;
    const ratio = limitMb > 0 ? mem.usedJSHeapSize / mem.jsHeapSizeLimit : 0;

    // 增长率（MB/s）
    const elapsedS = this.lastHeapSampleTime > 0
      ? (now - this.lastHeapSampleTime) / 1_000
      : 1;
    const growthRate = this.lastHeapMb > 0
      ? (usedMb - this.lastHeapMb) / elapsedS
      : 0;

    this.lastHeapMb = usedMb;
    this.lastHeapSampleTime = now;

    const level = ratio > HEAP_WARNING_RATIO ? 'critical'
      : ratio > 0.6 ? 'warning' : 'normal';
    const trend = growthRate >= HEAP_GROWTH_SPIKE_RATE ? 'spike'
      : growthRate >= HEAP_GROWTH_GROWING_RATE ? 'growing' : 'stable';

    const activeVideoCount = this.getActiveVideoCount();
    const domNodeCount = document.querySelectorAll('*').length;

    // normal 级别跳过上报，session_summary 已覆盖基线数据
    if (level !== 'normal') {
      reportCustomEvent(
        'memory_snapshot',
        {
          used_heap_mb: Math.round(usedMb),
          total_heap_mb: Math.round(totalMb),
          heap_usage_ratio: Math.round(ratio * 100) / 100,
          heap_limit_mb: Math.round(limitMb),
          heap_growth_rate: Math.round(growthRate * 100) / 100,
          active_video_count: activeVideoCount,
          dom_node_count: domNodeCount,
        },
        {
          page: this.getPage(),
          device_memory_gb: String((navigator as NavigatorWithMemory).deviceMemory ?? 'unknown'),
          level,
          trend,
          session_duration_s: String(this.getSessionDurationS()),
        },
      );
    }

    // 累积会话聚合样本
    this.memorySamples.push(Math.round(usedMb));

    // memory_warning：同会话最多 3 次
    if (ratio > HEAP_WARNING_RATIO && this.memoryWarnCount < MAX_MEMORY_WARN_COUNT) {
      this.memoryWarnCount += 1;
      reportCustomEvent(
        'memory_warning',
        { used_heap_mb: Math.round(usedMb), heap_usage_ratio: Math.round(ratio * 100) / 100 },
        {
          page: this.getPage(),
          device_memory_gb: String((navigator as NavigatorWithMemory).deviceMemory ?? 'unknown'),
        },
      );
    }

    this.checkPrecursor();
    this.adjustSamplingRate();
  }

  // ─── 崩溃根因分类（P0-20）─────────────────────────────────────────────────
  // 把「崩了 N 次」变成「60% 是 oom_media」这样可动手的结论，而非单纯的次数统计。
  private classifyCrash(params: {
    heapRatio: number;
    videoCount: number;
    domNodeCount: number;
    longTaskTotalMs: number;
  }): 'oom_heap' | 'oom_media' | 'gpu_lost' | 'long_freeze' | 'unknown' {
    if (this.contextLostCount > 0) return 'gpu_lost';
    if (params.videoCount > VIDEO_PRECURSOR_COUNT) return 'oom_media';
    if (params.heapRatio > HEAP_PRECURSOR_RATIO) return 'oom_heap';
    if (params.longTaskTotalMs > 5_000) return 'long_freeze';
    return 'unknown';
  }

  // ─── 崩溃前兆 ──────────────────────────────────────────────────────────────

  private checkPrecursor(): void {
    if (this.precursorFired) return;

    const mem = this.getMemoryInfo();
    const ratio = mem && mem.jsHeapSizeLimit > 0
      ? mem.usedJSHeapSize / mem.jsHeapSizeLimit
      : 0;
    const activeVideoCount = this.getActiveVideoCount();
    const domNodeCount = document.querySelectorAll('*').length;
    const lowFpsPersisted = this.lowFpsStartTime !== null &&
      Date.now() - this.lowFpsStartTime > LOW_FPS_DURATION_MS;

    const conditions = {
      highHeap: ratio > HEAP_PRECURSOR_RATIO,
      lowFps: lowFpsPersisted,
      highVideo: activeVideoCount > VIDEO_PRECURSOR_COUNT,
      highDom: domNodeCount > DOM_PRECURSOR_COUNT,
    };

    const triggeredCount = Object.values(conditions).filter(Boolean).length;
    if (triggeredCount < 2) return;

    this.precursorFired = true;
    this.hadPrecursor = true;

    const triggerConditions = Object.entries(conditions)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .join(',');

    const usedMb = mem ? Math.round(mem.usedJSHeapSize / 1_048_576) : 0;
    const crashClass = this.classifyCrash({
      heapRatio: ratio,
      videoCount: activeVideoCount,
      domNodeCount,
      longTaskTotalMs: this.longTaskTotalMs,
    });

    reportCustomEvent(
      'crash_precursor',
      {
        used_heap_mb: usedMb,
        heap_usage_ratio: Math.round(ratio * 100) / 100,
        fps: Math.round(this.currentFps),
        active_video_count: activeVideoCount,
        dom_node_count: domNodeCount,
        session_duration_s: this.getSessionDurationS(),
      },
      {
        page: this.getPage(),
        device_memory_gb: String((navigator as NavigatorWithMemory).deviceMemory ?? 'unknown'),
        trigger_conditions: triggerConditions,
        crash_class: crashClass,
        gpu_renderer: this.gpuRenderer,
        cpu_cores: this.cpuCores,
      },
    );

    // 前兆触发后立即 flush，不等批量
    apmClient.getSender()?.flush();
  }

  // ─── pagehide 兜底 ─────────────────────────────────────────────────────────

  private setupPageHideListener(): void {
    window.addEventListener('pagehide', () => {
      const snapshot = this.buildSnapshot();
      reportCustomLog(
        'page_unload_snapshot',
        'warn',
        {
          used_heap_mb: snapshot.usedHeapMb,
          heap_usage_ratio: snapshot.heapUsageRatio,
          fps_last: snapshot.fpsLast,
          active_video_count: snapshot.activeVideoCount,
          dom_node_count: snapshot.domNodeCount,
          session_duration_s: snapshot.sessionDurationS,
          page: snapshot.page,
          device_memory_gb: snapshot.deviceMemoryGb,
          gpu_renderer: snapshot.gpuRenderer,
          had_precursor: snapshot.hadPrecursor ? 1 : 0,
          webgl_lost_count: snapshot.webglLostCount,
          unload_reason: document.hidden ? 1 : 0,
        },
      );

      // session_summary：全会话聚合汇报，供 APMPlus 大盘直接使用
      if (this.fpsSamples.length > 0 || this.memorySamples.length > 0) {
        const avgFps = this.fpsSamples.length > 0
          ? Math.round(this.fpsSamples.reduce((a, b) => a + b, 0) / this.fpsSamples.length)
          : -1;
        const minFps = this.fpsSamples.length > 0 ? Math.min(...this.fpsSamples) : -1;
        const avgMemMb = this.memorySamples.length > 0
          ? Math.round(this.memorySamples.reduce((a, b) => a + b, 0) / this.memorySamples.length)
          : -1;
        const maxMemMb = this.memorySamples.length > 0 ? Math.max(...this.memorySamples) : -1;

        reportCustomEvent(
          'session_summary',
          {
            avg_fps: avgFps,
            min_fps: minFps,
            avg_memory_mb: avgMemMb,
            max_memory_mb: maxMemMb,
            long_task_count: this.longTaskCount,
            long_task_total_ms: this.longTaskTotalMs,
            webgl_lost_count: this.contextLostCount,
            session_duration_s: snapshot.sessionDurationS,
            // 0/1 数值字段，AVG 直接等于崩溃率/前兆率，无需两条线做比值
            had_crash_int: this.contextLostCount > 0 ? 1 : 0,
            had_precursor_int: snapshot.hadPrecursor ? 1 : 0,
          },
          {
            page: snapshot.page,
            gpu_renderer: snapshot.gpuRenderer,
            device_memory_gb: snapshot.deviceMemoryGb,
          },
        );
      }

      apmClient.getSender()?.flush();

      // 正常收尾，供其它标签页/下次启动判定「本次是否正常退出」：
      // ① 先写「优雅退出标记」—— 独立 key，不会被其它标签页的注册表写回覆盖，
      //    是「本次正常退出」最可靠的证据；
      // ② 再尝试从注册表移除自己（单 key 读-改-写，可能被并发写覆盖或异常失败，
      //    失败时由①兜底，不会再被误判为崩溃）。
      // 若页面随后被硬杀（OOM/进程 kill）而此监听未执行，① ② 都不会写入，
      // 注册表里的记录会留下，由下次任意标签页启动时的 checkAndReportPreviousCrash()
      // 判定并补报 crash_confirmed。
      this.stopTabHeartbeat();
      writeCleanExitMarker(this.tabId);
      try {
        const registry = readTabRegistry();
        delete registry[this.tabId];
        writeTabRegistry(registry);
      } catch {
        // 忽略 localStorage 异常
      }
    }, { capture: true });
  }

  // ─── 长任务 ────────────────────────────────────────────────────────────────

  private setupLongTaskObserver(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration < LONG_TASK_REPORT_THRESHOLD_MS) continue;
          // attribution.name 只表达「框架上下文」（self / cross-origin-*），并不是脚本名，
          // 部分浏览器还会给 unknown —— 单靠它定位不到元凶，因此另外补两路信息：
          //   ① attribution_container：长任务发生在 iframe 内时给出容器来源；
          //   ② interaction*：长任务开始前 3s 内的最近一次用户交互（近似归因，
          //      「点了什么 → 主线程被阻塞」是最常见的因果链）。
          const attr = (entry as PerformanceLongTaskTiming).attribution?.[0];
          const attribution = attr?.name ?? 'unknown';
          const container = attr?.containerType
            ? `${attr.containerType}:${attr.containerSrc || attr.containerId || attr.containerName || ''}`.slice(0, 80)
            : '';
          const taskStartTs = performance.timeOrigin + entry.startTime;
          const near = this.getRecentInteraction(taskStartTs);

          this.longTaskCount += 1;
          this.longTaskTotalMs += Math.round(entry.duration);
          reportCustomEvent(
            'long_task',
            {
              duration_ms: Math.round(entry.duration),
              // 交互发生在长任务开始前多少毫秒；-1 = 该窗口内无交互（疑似非交互触发）。
              // 放 metrics（数值字段）而非 categories（仅接受字符串），便于看板做分布统计。
              interaction_before_ms: near ? Math.max(0, Math.round(taskStartTs - near.at)) : -1,
            },
            {
              page: this.getPage(),
              attribution,
              ...(container ? { attribution_container: container } : {}),
              interaction: near ? near.type : 'none',
              interaction_target: near ? near.target : '',
            },
          );
        }
      });
      observer.observe({ type: 'longtask', buffered: false });
    } catch {
      // longtask 类型不被所有浏览器支持，静默忽略
    }
  }

  /** 取「长任务开始前 3s 内的最近交互」（更早的与本次卡顿无因果）；无则 null */
  private getRecentInteraction(taskStartTs: number): { type: string; target: string; at: number } | null {
    const last = this.lastInteraction;
    if (!last) return null;
    const delta = taskStartTs - last.at;
    if (delta < 0 || delta > INTERACTION_ATTRIBUTION_WINDOW_MS) return null;
    return last;
  }

  /**
   * 交互记录：只写内存、不上报，供长任务归因使用。
   * 以 capture + passive 监听，不干预业务自身的事件处理与滚动性能。
   */
  private setupInteractionTracking(): void {
    if (this.interactionTrackingAttached) return;
    const handler = (e: Event) => {
      try {
        this.lastInteraction = {
          type: e.type,
          target: summarizeElement(e.target as Element | null),
          at: Date.now(),
        };
      } catch {
        // 忽略
      }
    };
    this.interactionHandler = handler;
    this.interactionTrackingAttached = true;
    for (const type of INTERACTION_EVENTS) {
      window.addEventListener(type, handler, { capture: true, passive: true });
    }
  }

  private teardownInteractionTracking(): void {
    if (!this.interactionHandler) return;
    for (const type of INTERACTION_EVENTS) {
      window.removeEventListener(type, this.interactionHandler, { capture: true });
    }
    this.interactionHandler = null;
    this.interactionTrackingAttached = false;
    this.lastInteraction = null;
  }

  // ─── 页面可见性 ────────────────────────────────────────────────────────────

  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopRAFLoop();
      } else {
        this.startRAFLoop();
      }
    });
  }

  // ─── 自适应采样频率 ────────────────────────────────────────────────────────

  private adjustSamplingRate(): void {
    const mem = this.getMemoryInfo();
    const ratio = mem && mem.jsHeapSizeLimit > 0
      ? mem.usedJSHeapSize / mem.jsHeapSizeLimit
      : 0;
    const shouldBeCritical = ratio > HEAP_PRECURSOR_RATIO || this.currentFps < LOW_FPS_THRESHOLD;

    if (shouldBeCritical && !this.inCriticalMode) {
      // 进入异常态：打开采样门控，走高频，把诊断数据采下来
      this.inCriticalMode = true;
      this.memorySamplingActive = true;
      this.fpsReportingActive = true;
      this.startMemorySampling();
      this.startFpsReporting();
    } else if (!shouldBeCritical && this.inCriticalMode && ratio < 0.6 && this.currentFps > 30) {
      // 持续恢复正常：关闭门控，回到零采样零上报（session_summary 仍每会话一条）
      this.inCriticalMode = false;
      this.memorySamplingActive = false;
      this.fpsReportingActive = false;
      this.stopMemorySampling();
      this.stopFpsReporting();
    }
  }

  // ─── 工具方法 ──────────────────────────────────────────────────────────────

  /** 窗口均值 fps（消除单帧抖动），窗口为空时退化为当前瞬时值 */
  private computeAvgFps(): number {
    if (this.fpsWindow.length === 0) return this.currentFps;
    const avgDelta = this.fpsWindow.reduce((a, b) => a + b, 0) / this.fpsWindow.length;
    return 1_000 / avgDelta;
  }

  private getMemoryInfo(): MemoryInfo | null {
    const perf = performance as PerformanceWithMemory;
    return perf.memory ?? null;
  }

  private getActiveVideoCount(): number {
    // 统计所有 video 元素（含已 removeAttribute('src') 但解码器未完全释放的）
    return document.querySelectorAll('video').length;
  }

  private getWaterfallItemCount(): number {
    return document.querySelectorAll('.waterfall-item').length;
  }

  private getPage(): string {
    return window.location.pathname;
  }

  private getSessionDurationS(): number {
    return Math.round((Date.now() - this.sessionStartTime) / 1_000);
  }

  private buildSnapshot(): SessionSnapshot {
    const mem = this.getMemoryInfo();
    const usedMb = mem ? Math.round(mem.usedJSHeapSize / 1_048_576) : 0;
    const ratio = mem && mem.jsHeapSizeLimit > 0
      ? Math.round((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100) / 100
      : 0;

    return {
      usedHeapMb: usedMb,
      heapUsageRatio: ratio,
      fpsLast: Math.round(this.currentFps),
      activeVideoCount: this.getActiveVideoCount(),
      domNodeCount: document.querySelectorAll('*').length,
      sessionDurationS: this.getSessionDurationS(),
      gpuRenderer: this.gpuRenderer,
      page: this.getPage(),
      deviceMemoryGb: String((navigator as NavigatorWithMemory).deviceMemory ?? 'unknown'),
      hadPrecursor: this.hadPrecursor,
      webglLostCount: this.contextLostCount,
    };
  }
}
